import moment from "moment";

export const GREEN = "#2C9C84";
export const BORDER = "#e9ecef";
export const MUTED = "#6c757d";
export const DANGER = "#e74c3c";

// Exact reservationType values stored in the DB (see AddReservation booking type options)
export const RESERVATION_TYPE = {
  UNCONFIRMED: "Unconfirmed Reservation",
  CONFIRMED: "Reservation",
  CHECKED_IN: "Check-in",
  CHECKED_OUT: "Check-out",
  CANCELLED: "Cancelled",
  NO_SHOW: "No-show",
};

export const STATUS_META = {
  [RESERVATION_TYPE.UNCONFIRMED]: {
    label: "Unconfirmed",
    background: "#fff3b0",
    color: "#8a6d1b",
    description: "This reservation has not been confirmed yet.",
    hint: "You can confirm or cancel this reservation using the actions menu.",
  },
  [RESERVATION_TYPE.CONFIRMED]: {
    label: "Confirmed",
    background: "#d4f4e3",
    color: "#1c7c53",
    description: "This reservation is confirmed.",
    hint: "You can check the guest in on arrival, or mark them as a no-show.",
  },
  [RESERVATION_TYPE.CHECKED_IN]: {
    label: "Checked In",
    background: "#ddecff",
    color: "#1b5fa8",
    description: "The guest has checked in and is currently staying.",
    hint: "Charges and payments can be recorded until the guest is checked out.",
  },
  [RESERVATION_TYPE.CHECKED_OUT]: {
    label: "Checked Out",
    background: "#fff2cc",
    color: "#8a6d1b",
    description: "The stay is complete and the guest has checked out.",
    hint: "This reservation is closed, so charges and payments are read-only.",
  },
  [RESERVATION_TYPE.CANCELLED]: {
    label: "Cancelled",
    background: "#ffd6d6",
    color: "#a72828",
    description: "This reservation has been cancelled.",
    hint: "Cancelled reservations are read-only and kept for reporting only.",
  },
  [RESERVATION_TYPE.NO_SHOW]: {
    label: "No-show",
    background: "#f3e5ff",
    color: "#6b3f9e",
    description: "The guest did not arrive for this reservation.",
    hint: "No-show reservations are read-only and kept for reporting only.",
  },
};

export const normalizeType = (reservationType = "") => {
  const type = String(reservationType).trim();
  // "Cancelled (hide)" appears in some older records / UI labels
  if (type === "Cancelled (hide)") return RESERVATION_TYPE.CANCELLED;
  return STATUS_META[type] ? type : RESERVATION_TYPE.UNCONFIRMED;
};

/**
 * Allowed status transitions. A reservation only moves forward one step at a
 * time, and checked-out / cancelled / no-show are terminal.
 */
export const getStatusActions = (reservationType) => {
  switch (reservationType) {
    case RESERVATION_TYPE.UNCONFIRMED:
      return [
        {
          value: RESERVATION_TYPE.CONFIRMED,
          label: "Confirm Reservation",
          variant: "primary",
          message: "Reservation confirmed.",
        },
        {
          value: RESERVATION_TYPE.CANCELLED,
          label: "Mark as Cancelled",
          variant: "warning",
          message: "Reservation cancelled.",
        },
      ];
    case RESERVATION_TYPE.CONFIRMED:
      return [
        {
          value: RESERVATION_TYPE.CHECKED_IN,
          label: "Check In",
          variant: "primary",
          message: "Guest checked in.",
        },
        {
          value: RESERVATION_TYPE.NO_SHOW,
          label: "Mark as No-show",
          variant: "warning",
          message: "Reservation marked as no-show.",
        },
      ];
    case RESERVATION_TYPE.CHECKED_IN:
      return [
        {
          value: RESERVATION_TYPE.CHECKED_OUT,
          label: "Check Out",
          variant: "primary",
          message: "Guest checked out.",
        },
      ];
    default:
      return [];
  }
};

export const PAYMENT_LABELS = {
  advance: "Advance",
  installment: "Installment",
  final: "Final Amount",
  settlement: "Settlement (Refund)",
};

export const PAYMENT_TYPE_OPTIONS = [
  { value: "advance", label: "Advance" },
  { value: "installment", label: "Installment" },
  { value: "final", label: "Final Amount" },
  { value: "settlement", label: "Settlement (Refund)" },
];

/** Only "cash" and "bank transfer" are ever stored. */
export const formatPaymentMode = (mode) =>
  mode === "bank transfer" ? "Bank Transfer" : "Cash";

export const createEmptyPaymentEntry = () => ({
  amount: "",
  mode: "cash",
  bankDetails: "",
  receiptUrl: "",
  remarks: "",
  recordedAt: "",
});

export const createDefaultSettlement = () => ({
  enabled: false,
  amount: "",
  mode: "cash",
  receiptUrl: "",
  remarks: "",
  recordedAt: "",
});

export const nowLocalDateTime = () => moment().format("YYYY-MM-DDTHH:mm");

export const createPaymentDraft = (type = "advance") => ({
  type,
  amount: "",
  mode: "cash",
  remarks: "",
  recordedAt: nowLocalDateTime(),
});

/** Firestore timestamps, JS dates and plain strings all reach this screen. */
export const toMoment = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === "function") return moment(value.toDate());
  const parsed = moment(
    value,
    [moment.ISO_8601, "YYYY-MM-DD", "YYYY-MM-DDTHH:mm", "YYYY-MM-D"],
    false
  );
  return parsed.isValid() ? parsed : null;
};

export const toDateTimeLocal = (value) => {
  const parsed = toMoment(value);
  return parsed ? parsed.format("YYYY-MM-DDTHH:mm") : "";
};

export const formatDate = (value) => {
  const parsed = toMoment(value);
  return parsed ? parsed.format("DD MMM YYYY") : "-";
};

export const formatDateTime = (value) => {
  const parsed = toMoment(value);
  return parsed ? parsed.format("DD MMM YYYY, hh:mm A") : "-";
};

export const formatClock = (value) => {
  if (!value) return "";
  const parsed = moment(value, ["HH:mm", "hh:mm A", "HH:mm:ss"], false);
  return parsed.isValid() ? parsed.format("hh:mm A") : String(value);
};

export const formatMoney = (value) =>
  `PKR ${Math.round(Number(value) || 0).toLocaleString("en-US")}`;

export const renderValue = (value) =>
  value === null || value === undefined || value === "" ? "-" : value;

/**
 * Visible reservation number shown across the app.
 * Derived from the Firestore doc id (last 6 characters, uppercased).
 * Accepts a reservation object or a raw id string.
 */
export const getReservationNo = (reservationOrId) => {
  const id =
    typeof reservationOrId === "string"
      ? reservationOrId
      : reservationOrId?.id || "";
  if (!id) return "-";
  return id.slice(-6).toUpperCase();
};

/** @deprecated Prefer getReservationNo — alias kept for older imports. */
export const formatReference = getReservationNo;

export const getFileName = (url) => {
  if (!url) return "";
  const clean = decodeURIComponent(String(url).split("?")[0]);
  const name = clean.split("/").pop() || "receipt";
  return name.replace(/^\d+_/, "");
};

export const sumServices = (services = []) =>
  services.reduce(
    (sum, service) =>
      sum + Number(service.quantity || 0) * Number(service.rate || 0) + Number(service.tax || 0),
    0
  );

/**
 * The invoice figures: services are charged on top of the room total, and the
 * settlement is a refund owed back to the guest, so it is excluded from what was
 * collected and added back onto the remaining balance.
 */
export const computeMoney = ({
  reservation,
  payments = {},
  settlement = {},
  totalServicesPrice = 0,
}) => {
  const roomTotal = Number(reservation?.totalPrice || 0);
  const taxes = Number(reservation?.taxes || 0);
  const services = Number(totalServicesPrice || 0);
  const amountCharged = roomTotal + taxes + services;
  const collected = ["advance", "installment", "final"].reduce(
    (sum, key) => sum + Number(payments[key]?.amount || 0),
    0
  );
  const settlementAmount = Number(settlement?.amount || 0);

  return {
    roomTotal,
    taxes,
    services,
    withTax: roomTotal + taxes,
    amountCharged,
    collected,
    settlement: settlementAmount,
    balanceAmount: amountCharged - collected,
    remainingBalance: amountCharged - (collected - settlementAmount),
  };
};

/** Stored alongside every payment write so reports stay in step with the invoice. */
export const buildPaymentSummary = (money) => ({
  amountCharged: money.amountCharged,
  collectedAmount: money.collected,
  balanceAmount: money.balanceAmount,
  settlement: money.settlement,
  remainingBalance: money.remainingBalance,
  grandTotal: money.amountCharged,
  netPayable: Math.max(money.amountCharged - money.settlement, 0),
  totalPaid: money.collected,
  balanceDue: money.remainingBalance,
});

/** bookingNotes is a single string; multiple notes are joined with ` | `. */
export const parseBookingNotes = (value) =>
  String(value || "")
    .split("|")
    .map((note) => note.trim())
    .filter(Boolean);

export const joinBookingNotes = (notes) =>
  (notes || [])
    .map((note) => String(note || "").trim())
    .filter(Boolean)
    .join(" | ");
